import React, { useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";

export default function Testimonials({ items = [] }) {
    const [idx, setIdx] = useState(0);
    if (items.length === 0) return null;
    const t = items[idx];

    const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
    const next = () => setIdx((i) => (i + 1) % items.length);

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
        >
            <div className="absolute top-1/2 -translate-y-1/2 left-0 w-[400px] h-[400px] rounded-full bg-brand/10 blur-[140px] pointer-events-none" aria-hidden />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex items-end justify-between gap-6 mb-14">
                    <div>
                        <div className="mono-label mb-4">05 · Voices</div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tighter">
                            What clients <span className="text-brand">say</span>.
                        </h2>
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                        <button
                            onClick={prev}
                            data-testid="testimonial-prev"
                            className="w-11 h-11 rounded-full border border-white/10 text-ink-200 hover:text-white hover:border-white/30 transition-colors flex items-center justify-center"
                            aria-label="Previous"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            onClick={next}
                            data-testid="testimonial-next"
                            className="w-11 h-11 rounded-full bg-brand hover:bg-brand-hover text-white transition-colors flex items-center justify-center"
                            aria-label="Next"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>

                <div className="relative p-8 lg:p-14 rounded-3xl border border-white/5 bg-ink-800/40 backdrop-blur" data-testid="testimonial-active">
                    <Quote size={48} className="text-brand/40 mb-6" />
                    <p className="font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-tight tracking-tight">
                        &ldquo;{t.quote}&rdquo;
                    </p>
                    <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {t.author_avatar_url ? (
                                    <img
                                        src={t.author_avatar_url}
                                        alt={t.author_name}
                                        className="w-full h-full object-contain p-2"
                                        onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                            e.currentTarget.parentElement.innerHTML = `<span class='font-display text-xl text-black'>${t.author_name[0]}</span>`;
                                        }}
                                    />
                                ) : (
                                    <span className="font-display text-xl text-black">{t.author_name[0]}</span>
                                )}
                            </div>
                            <div className="min-w-0">
                                <div className="text-white font-medium">{t.author_name}</div>
                                <div className="text-xs text-ink-200">
                                    {t.author_role}
                                    {t.author_role && t.author_company && " · "}
                                    {t.author_company}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-1">
                            {Array.from({ length: t.rating || 5 }).map((_, i) => (
                                <Star key={i} size={16} className="text-brand fill-brand" />
                            ))}
                        </div>
                    </div>

                    {/* Dots indicator */}
                    <div className="mt-8 flex items-center justify-center gap-2">
                        {items.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                data-testid={`testimonial-dot-${i}`}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === idx ? "w-8 bg-brand" : "w-1.5 bg-white/15 hover:bg-white/30"
                                }`}
                                aria-label={`Go to testimonial ${i + 1}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Mobile prev/next */}
                <div className="sm:hidden flex items-center justify-center gap-2 mt-4">
                    <button onClick={prev} className="w-11 h-11 rounded-full border border-white/10 text-ink-200 flex items-center justify-center">
                        <ChevronLeft size={16} />
                    </button>
                    <button onClick={next} className="w-11 h-11 rounded-full bg-brand text-white flex items-center justify-center">
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </section>
    );
}
